// 版本管理弹窗（feat/version-lifecycle）—— 从左上角「管理版本…」入口打开。
// 大版本列表：状态徽标 + 默认标记 + 编辑中 + 操作（编辑 / 查看改动 / 设为默认 / 回滚 / 新建大版本）。
// 「保存」「发布」不在这里——它们在各编辑段右上角；这里只管版本的切换/默认/回滚/新建。
// active/superseded 是后端按 serving 指针派生的 label，这里只展示。
import React, { useEffect, useState } from 'react';
import { XPrim } from '../../primitives.jsx';
import { XApi } from '../../services.js';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../../ui/index.js';
import { DiffView, ReviewBadge } from './version-shared.jsx';
import { ReviewReportDialog } from './review-report.jsx';
import { getMajorStatusBadge } from './version-panel-status.js';

const STATE_TEXT = { DRAFT: '草稿', SANDBOX: '沙箱', IN_REVIEW: '审核中' };

const StateBadge = ({ major, serviceStatus }) => {
  const badge = getMajorStatusBadge(major, serviceStatus);
  return (
    <span title={badge.title} style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.fg }}>
      {badge.text}
    </span>
  );
};

// 设计系统 .btn 类（不自造）。primary = 实心主按钮。
const Btn = ({ children, onClick, disabled, primary }) => (
  <button
    type="button"
    className={`btn btn-sm${primary ? ' btn-primary' : ''}`}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
);

// 无框文字操作（历史行用，避免一排线框按钮的拥挤感）。muted = 次要动作（灰）。
const LinkBtn = ({ children, onClick, disabled, muted, title }) => (
  <button type="button" className={`vm-link${muted ? ' muted' : ''}`} disabled={disabled} onClick={onClick} title={title}>
    {children}
  </button>
);

/**
 * 版本管理弹窗。selectedMajor = 当前正在编辑的大版本（由左上角切换器控制）。
 * onSelectMajor(major) 切换编辑目标（并关闭弹窗）；onApiRefreshed 让外层同步最新 api。
 */
export const VersionManagerDialog = ({ open, api, selectedMajor, onClose, onSelectMajor, onApiRefreshed }) => {
  const { toast } = XPrim.useToast();
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null); // 加载失败原因（与「真的没版本」区分开，不再都显示「暂无」）
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null); // 当前展开「历史/审核」的大版本（默认收起）
  const [showAllHist, setShowAllHist] = useState(false); // 历史默认只显示最近 3 条
  const [diff, setDiff] = useState(null); // { major, loading, data }
  const [confirm, setConfirm] = useState(null); // { title, body, label, run }
  const [rollbackTarget, setRollbackTarget] = useState(null); // { major, revisionId, revision, currentRevision }
  const [rollbackReason, setRollbackReason] = useState('');
  const [reviewDlg, setReviewDlg] = useState(null); // { revId, label }

  // 手动重载（管理动作后调用）。普通函数 —— 不放进任何依赖数组，避免 toast 不稳定引发循环。
  const reload = async () => {
    if (!api?.id) { setErr('未取到当前 API（请关闭重开版本管理）'); setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      setOv(await XApi.apiSvc.versionOverview(api.id));
    } catch (e) {
      setErr(e?.message || '加载版本失败');
      toast({ title: e?.message || '加载版本失败', type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // 打开时拉一次。依赖只放稳定值 [open, api?.id]：之前依赖 load(含不稳定的 toast)
  // 会每次渲染重建 → effect 反复触发 → setLoading(true) 永远抢在 finally 前 → 卡「加载中」。
  useEffect(() => {
    if (!open) {
      setRollbackTarget(null);
      setRollbackReason('');
      return;
    }
    let cancelled = false;
    setOv(null);
    setExpanded(null); setShowAllHist(false); // 每次打开都收起历史
    // api 还没装好就打开 → 不是「没版本」，是拿不到 API。明确报错，别误显示「暂无版本数据」。
    if (!api?.id) { setLoading(false); setErr('未取到当前 API（请关闭重开版本管理）'); return; }
    setLoading(true); setErr(null);
    XApi.apiSvc.versionOverview(api.id)
      .then((d) => { if (!cancelled) setOv(d); })
      .catch((e) => { if (!cancelled) setErr(e?.message || '加载版本失败'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api?.id]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const result = await fn();
      toast({ title: typeof okMsg === 'function' ? okMsg(result) : okMsg, type: 'success' });
      await reload();
      const fresh = await XApi.apiSvc.get(api.id).catch(() => null);
      if (fresh) onApiRefreshed?.(fresh);
      return result;
    } catch (e) {
      toast({ title: e?.message || '操作失败', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const editMajor = (m) => { onSelectMajor?.(m); onClose?.(); };
  const newMajor = () => act(() => XApi.apiSvc.createMajor(api.id), '已新建大版本');
  const setDefault = (m) =>
    setConfirm({
      title: '切换默认大版本',
      body: `把默认版本切到 v${m}？消费者不写版本号直接调用（host/… 而不是 host/v${m}/…）时会走 v${m}；明确带 /v1、/v2 的调用不受影响、各走各的。`,
      label: '切换',
      run: () => act(() => XApi.apiSvc.setDefaultMajor(api.id, m), `默认已切到 v${m}`),
    });
  const rollbackBlockedReason = (m) => {
    if (!['ACTIVE', 'SUSPENDED'].includes(api?.status)) return `当前服务状态为 ${api?.status || '未知'}，仅上线或暂停的服务可回滚`;
    if (m.deprecated) return `v${m.major} 已弃用，需先恢复上线`;
    if (!m.servingRevisionId) return `v${m.major} 没有当前 serving 修订，不能回滚`;
    return null;
  };
  const openRollback = (m, h) => {
    if (rollbackBlockedReason(m) || h.isServing) return;
    const current = m.history.find((item) => item.isServing);
    setRollbackReason('');
    setRollbackTarget({
      major: m.major,
      revisionId: h.id,
      revision: h.revision,
      currentRevision: current?.revision,
    });
  };
  const closeRollback = () => {
    if (busy) return;
    setRollbackTarget(null);
    setRollbackReason('');
  };
  const runRollback = async () => {
    const reason = rollbackReason.trim();
    if (!rollbackTarget || !reason) return;
    setBusy(true);
    try {
      const result = await XApi.apiSvc.rollbackMajor(
        api.id,
        rollbackTarget.major,
        rollbackTarget.revisionId,
        reason,
      );
      if (result?.changed === false) {
        toast({ title: `v${rollbackTarget.major} rev ${rollbackTarget.revision} 已经在 serving，无需回滚`, type: 'success' });
      } else {
        const statusHint = result?.serviceStatus === 'SUSPENDED'
          ? '；服务仍保持暂停，恢复后生效'
          : '；流量已切换';
        toast({ title: `已回滚到 v${rollbackTarget.major} rev ${rollbackTarget.revision}${statusHint}`, type: 'success' });
      }
      setRollbackTarget(null);
      setRollbackReason('');
      await reload();
      const fresh = await XApi.apiSvc.get(api.id).catch(() => null);
      if (fresh) onApiRefreshed?.(fresh);
    } catch (e) {
      toast({ title: e?.message || '回滚失败', type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const setDeprecated = (m, deprecated) =>
    deprecated
      ? setConfirm({
          title: m.isDefault ? '下架整个服务' : '弃用大版本',
          body: m.isDefault
            ? `下架默认版本 v${m.major}？这会暂停整个服务：所有大版本都将停止对外流量，并从市场、搜索和模型目录中隐藏。配置、审核和历史数据会保留，之后可在这里「恢复上线」。`
            : `弃用 v${m.major}？该版本将下线：网关不再路由（/vN、X-Xapi-Version 都调不到），外部也搜不到。数据保留、随时可「恢复上线」。`,
          label: m.isDefault ? '确认下架' : '弃用',
          run: () => act(
            () => XApi.apiSvc.setMajorDeprecated(api.id, m.major, true),
            m.isDefault ? '默认版本已下架，整个服务已暂停' : `v${m.major} 已弃用`,
          ),
        })
      : act(
          () => XApi.apiSvc.setMajorDeprecated(api.id, m.major, false),
          (result) => {
            if (!m.isDefault) return `v${m.major} 已恢复上线`;
            return result?.serviceStatus === 'ACTIVE'
              ? '默认版本已恢复，整个服务已恢复上线'
              : '默认版本已恢复，但服务仍处于暂停状态';
          },
        );

  const openDiff = async (m) => {
    setDiff({ major: m.major, loading: true, data: null });
    try {
      const d = await XApi.apiSvc.diffPreview(api.id, m.major);
      setDiff((s) => (s ? { ...s, loading: false, data: d } : s));
    } catch (e) {
      toast({ title: e?.message || '加载改动失败', type: 'error' });
      setDiff(null);
    }
  };

  const runConfirm = () => {
    const r = confirm?.run;
    setConfirm(null);
    r && r();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={680}>
        <DialogHeader title="版本管理" onClose={onClose} />
        <DialogBody>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.6, maxWidth: 470 }}>
              普通大版本可以独立上线或弃用，消费者用 <code>/v1</code>、<code>/v2</code> 分别调用。
              「默认版本」= 消费者<b>不写版本号</b>直接调用时走哪个（如 <code>host/chat</code> 不带 /vN → 默认版本）。
              下架默认版本会暂停<b>整个服务</b>，所有大版本都停止对外流量。
              「编辑」切到该版本，改动在「基本信息」「管理 API」里保存、发布。
            </div>
            <Btn disabled={busy} onClick={newMajor}>+ 新建大版本</Btn>
          </div>

          {loading ? (
            <div style={{ padding: 24, color: 'var(--fg-3)' }}>加载中…</div>
          ) : err ? (
            <div style={{ padding: 24, color: 'var(--fg-3)', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ color: '#dc2626' }}>加载版本失败：{err}</div>
              <Btn disabled={busy} onClick={reload}>重试</Btn>
            </div>
          ) : !ov || !ov.majors?.length ? (
            <div style={{ padding: 24, color: 'var(--fg-3)' }}>该 API 还没有任何版本。</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {ov.majors.map((m) => (
                <div key={m.major} style={{
                  border: `1px solid ${m.major === selectedMajor ? 'rgba(37,99,235,0.45)' : 'var(--bd-1)'}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  background: m.major === selectedMajor ? 'rgba(37,99,235,0.06)' : 'var(--bg-0)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg-0)' }}>{m.label}</span>
                    <StateBadge major={m} serviceStatus={api?.status} />
                    {m.isDefault && (
                      <span style={{ fontSize: 11, color: 'var(--fg-3)', border: '1px solid var(--bd-1)', borderRadius: 10, padding: '1px 7px' }}>默认</span>
                    )}
                    {m.major === selectedMajor && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,0.13)', borderRadius: 6, padding: '2px 8px' }}>正在编辑</span>
                    )}
                    {m.working && (
                      <span title={`工作副本状态：${STATE_TEXT[m.working.state] || m.working.state}`} style={{ fontSize: 11, color: '#b45309' }}>✎ 有未发布的改动</span>
                    )}
                    {m.working?.review && <ReviewBadge outcome={m.working.review.outcome} />}
                    <div style={{ flex: 1 }} />
                    {m.working?.review && <Btn disabled={busy} onClick={() => setReviewDlg({ revId: m.working.id, label: `${m.label} 工作副本` })}>审核详情</Btn>}
                    {m.working && <Btn disabled={busy} onClick={() => openDiff(m)}>查看改动</Btn>}
                    {m.major !== selectedMajor && <Btn disabled={busy} onClick={() => editMajor(m.major)}>编辑</Btn>}
                    {m.live && !m.isDefault && <Btn disabled={busy} onClick={() => setDefault(m.major)}>设为默认</Btn>}
                    {m.deprecated
                      ? <Btn disabled={busy} onClick={() => setDeprecated(m, false)}>{m.isDefault ? '恢复默认版本' : '恢复版本'}</Btn>
                      : (m.live || m.status === 'active') && <Btn disabled={busy} onClick={() => setDeprecated(m, true)}>{m.isDefault ? '下架服务' : '弃用版本'}</Btn>}
                    {(m.history.length >= 1 || m.working) && (
                      <Btn disabled={busy} onClick={() => { setShowAllHist(false); setExpanded(expanded === m.major ? null : m.major); }}>
                        历史/审核 {expanded === m.major ? '▴' : '▾'}
                      </Btn>
                    )}
                  </div>

                  {expanded === m.major && (() => {
                    // 默认只展示最近 3 条；更多点「查看更多」。history 已按 revision 倒序（新在前）。
                    const shown = showAllHist ? m.history : m.history.slice(0, 3);
                    const hidden = m.history.length - shown.length;
                    return (
                      <div style={{ marginTop: 10, borderTop: '1px solid var(--bd-1)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>修订记录 · 点「审核详情」看每条 rev 的审核结果与过程</div>
                        {/* 当前工作修订（含 审核中/已驳回/沙箱）置顶，让失败/未发布的修订也可见、可追溯 */}
                        {m.working && (
                          <div className="vm-hist-row" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '6px 8px', borderRadius: 6, margin: '0 -8px' }}>
                            <span style={{ color: 'var(--fg-1)', fontFamily: 'monospace', minWidth: 52 }}>rev {m.working.revision}</span>
                            {m.working.review
                              ? <ReviewBadge outcome={m.working.review.outcome} />
                              : <span style={{ fontSize: 11, color: '#b45309' }}>{STATE_TEXT[m.working.state] || m.working.state}</span>}
                            <span style={{ color: 'var(--fg-4)', fontSize: 11, whiteSpace: 'nowrap' }}>工作副本·未发布</span>
                            <div style={{ flex: 1 }} />
                            {m.working.review && <LinkBtn disabled={busy} onClick={() => setReviewDlg({ revId: m.working.id, label: `${m.label} 工作副本` })}>审核详情</LinkBtn>}
                            <span style={{ width: 56, display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                              <LinkBtn muted disabled={busy} onClick={() => openDiff(m)}>查看改动</LinkBtn>
                            </span>
                          </div>
                        )}
                        {shown.map((h) => {
                          const rollbackBlocked = rollbackBlockedReason(m);
                          return (
                            <div key={h.id} className="vm-hist-row" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '6px 8px', borderRadius: 6, margin: '0 -8px' }}>
                              <span style={{ color: 'var(--fg-1)', fontFamily: 'monospace', minWidth: 52 }}>rev {h.revision}</span>
                              {h.isServing && (
                                <span style={{ color: '#16a34a', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />在跑
                                </span>
                              )}
                              <div style={{ flex: 1 }} />
                              {h.review && <LinkBtn disabled={busy} onClick={() => setReviewDlg({ revId: h.id, label: `${m.label} rev ${h.revision}` })}>审核详情</LinkBtn>}
                              {/* 固定槽位：非在跑显示「回滚到此」，在跑留空 → 「审核详情」各行对齐 */}
                              <span style={{ width: 56, display: 'inline-flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                {!h.isServing && (
                                  <LinkBtn
                                    muted
                                    disabled={busy || !!rollbackBlocked}
                                    title={rollbackBlocked || `回滚到 v${m.major} rev ${h.revision}`}
                                    onClick={() => openRollback(m, h)}
                                  >
                                    回滚到此
                                  </LinkBtn>
                                )}
                              </span>
                            </div>
                          );
                        })}
                        {hidden > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <LinkBtn muted onClick={() => setShowAllHist(true)}>查看更多 {hidden} 条历史 ↓</LinkBtn>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn btn-sm" onClick={onClose}>关闭</button>
        </DialogFooter>
      </Dialog>

      {/* Revision rollback 是独立生命周期操作：必须记录原因，并清楚展示服务状态影响。 */}
      <Dialog open={open && !!rollbackTarget} onClose={closeRollback} maxWidth={480}>
        <DialogHeader title="回滚修订" onClose={closeRollback} />
        <DialogBody>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.65 }}>
            将 v{rollbackTarget?.major} 从 rev {rollbackTarget?.currentRevision ?? '当前版本'} 回滚到 rev {rollbackTarget?.revision}。
            目标修订已经通过审核，不会重新审核；该大版本正在审核的工作修订会退回沙箱并保留记录。
          </div>
          <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: 'var(--bg-1)', color: 'var(--fg-2)', fontSize: 12, lineHeight: 1.6 }}>
            {api?.status === 'SUSPENDED'
              ? '当前服务已暂停：本次只更新 serving 指针，不会恢复流量；服务恢复后使用目标修订。'
              : '当前服务已上线：确认后会立即把该大版本的流量切换到目标修订。'}
          </div>
          <label style={{ display: 'block', marginTop: 14, fontSize: 12, fontWeight: 600, color: 'var(--fg-1)' }} htmlFor="rollback-reason">
            回滚原因 <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            id="rollback-reason"
            className="input"
            rows={4}
            maxLength={2000}
            autoFocus
            disabled={busy}
            value={rollbackReason}
            onChange={(e) => setRollbackReason(e.target.value)}
            placeholder="说明为什么回滚，便于后续审计和故障复盘"
            style={{ width: '100%', marginTop: 6, resize: 'vertical' }}
          />
          <div style={{ marginTop: 4, textAlign: 'right', fontSize: 11, color: 'var(--fg-4)' }}>{rollbackReason.length}/2000</div>
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={closeRollback}>取消</button>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !rollbackReason.trim()} onClick={runRollback}>确认回滚</button>
        </DialogFooter>
      </Dialog>

      {/* 查看改动（只读 diff） */}
      <Dialog open={!!diff} onClose={() => setDiff(null)} maxWidth={560}>
        <DialogHeader title={`本次改动${diff ? ` · v${diff.major}` : ''}`} onClose={() => setDiff(null)} />
        <DialogBody>
          {diff?.loading ? <div style={{ color: 'var(--fg-3)' }}>计算中…</div> : <DiffView diff={diff?.data} />}
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn btn-sm" onClick={() => setDiff(null)}>关闭</button>
        </DialogFooter>
      </Dialog>

      {/* 二次确认（切默认 / 回滚）—— Base UI Dialog */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth={420}>
        <DialogHeader title={confirm?.title} onClose={() => setConfirm(null)} />
        <DialogBody>
          <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.6 }}>{confirm?.body}</div>
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn btn-sm" onClick={() => setConfirm(null)}>取消</button>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={runConfirm}>{confirm?.label}</button>
        </DialogFooter>
      </Dialog>

      {/* 审核详情（完整报告）*/}
      <ReviewReportDialog
        open={!!reviewDlg}
        apiId={api?.id}
        revId={reviewDlg?.revId}
        revLabel={reviewDlg?.label}
        onClose={() => setReviewDlg(null)}
      />
    </>
  );
};

export default VersionManagerDialog;
